import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import useScreenSize from '../../core/hooks/useScreenSize';
import { Swiper, SwiperSlide } from 'swiper/react';
import CategoryLink from '../CategoryLink/CategoryLink';
import { Icon } from '../common/Icons';
import 'swiper/css';
import {
  StyledContainer,
  StyledStoryContainer,
  StyledPhoto,
  StyledPhotoLink,
  StyledContentWrapper,
  StyledTitleWrapper,
  StyledLink,
  StyledTitle,
  StyledAuthor,
  StyledButton,
  StyledButtonsWrapper,
  StyledLikeButton,
  StyledOptionsButton,
  StyledFullWidth,
} from './StorySlider.styles';

function StorySlider({ stories, loadMore, onLoadMoreClick, sidebar, mobileVertical, onClick }) {
  const [size] = useScreenSize();
  const [storage, setStorage] = useState();

  useEffect(() => {
    const storageTest = JSON.parse(localStorage.getItem('likedExperiences'));
    if (storageTest instanceof Array) {
      setStorage(storageTest);
    }
  }, []);

  const typeConditional = useMemo(() => (sidebar ? 'sidebar' : undefined), [sidebar]);

  const onLikeClick = id => {
    onClick(id);
    if (storage.includes(id)) {
      setStorage(prev => prev.filter(element => element !== id));
    } else {
      setStorage(prev => [...prev, id]);
    }
  };

  if (!stories.length) return null;

  return (
    <div>
      <StyledContainer type={typeConditional}>
        {size < 992 && !mobileVertical ? (
          <Swiper
            spaceBetween={15}
            slidesPerView="auto"
            slidesOffsetBefore={26}
            slidesOffsetAfter={30}
          >
            {stories.map((story, key) => (
              <SwiperSlide key={key}>
                {story.category && (
                  <CategoryLink
                    link={story.category.link ? `/category/${story.category.link}` : '/'}
                    color={story.category.type && story.category.type}
                    name={story.category.name && story.category.name}
                  />
                )}
                <StyledContentWrapper>
                  <StyledLink to={story.url}>
                    {story.photo && <StyledPhoto src={story.photo} alt={story.photoAlt} />}
                  </StyledLink>
                  {story.author && <StyledAuthor>{story.author}</StyledAuthor>}
                  <StyledTitleWrapper>
                    {story.title && <StyledTitle>{story.title}</StyledTitle>}
                    <StyledButtonsWrapper>
                      {story.experienceId ? (
                        <StyledLikeButton
                          type="button"
                          onClick={() => onLikeClick(story.experienceId)}
                        >
                          {storage && storage.includes(story.experienceId) ? (
                            <Icon.HeartBig />
                          ) : (
                            <Icon.HeartOutline />
                          )}
                        </StyledLikeButton>
                      ) : (
                        <StyledLikeButton type="button">
                          <Icon.HeartBig />
                        </StyledLikeButton>
                      )}
                      <StyledOptionsButton type="button">
                        <Icon.Options />
                      </StyledOptionsButton>
                    </StyledButtonsWrapper>
                  </StyledTitleWrapper>
                </StyledContentWrapper>
              </SwiperSlide>
            ))}
            {loadMore && (
              <SwiperSlide>
                <StyledButton onClick={onLoadMoreClick}>
                  <Icon.Plus aria-hidden />
                  <span>Load More</span>
                </StyledButton>
              </SwiperSlide>
            )}
          </Swiper>
        ) : (
          <>
            {stories.map((story, key) => {
              const categoryLink = () => (
                <CategoryLink
                  link={story.category.link ? `/category/${story.category.link}` : '/'}
                  color={story.category.type && story.category.type}
                  name={story.category.name && story.category.name}
                />
              );

              return (
                <StyledStoryContainer key={key} type={typeConditional}>
                  {!sidebar && story.category && categoryLink()}
                  {story.photo && (
                    <StyledPhotoLink to={story.url} type={typeConditional}>
                      <StyledPhoto
                        src={story.photo}
                        alt={story.photoAlt}
                        type={typeConditional}
                        mobileShort={mobileVertical}
                      />
                    </StyledPhotoLink>
                  )}
                  <StyledFullWidth>
                    {sidebar && story.category && categoryLink()}
                    <div>
                      {story.author && <StyledAuthor>{story.author}</StyledAuthor>}

                      <StyledTitleWrapper>
                        {story.title && <StyledTitle>{story.title}</StyledTitle>}
                        <StyledButtonsWrapper>
                          {story.experienceId ? (
                            <StyledLikeButton
                              type="button"
                              onClick={() => onLikeClick(story.experienceId)}
                            >
                              {storage && storage.includes(story.experienceId) ? (
                                <Icon.HeartBig />
                              ) : (
                                <Icon.HeartOutline />
                              )}
                            </StyledLikeButton>
                          ) : (
                            <StyledLikeButton type="button">
                              <Icon.HeartOutline />
                            </StyledLikeButton>
                          )}
                          <StyledOptionsButton type="button">
                            <Icon.Options />
                          </StyledOptionsButton>
                        </StyledButtonsWrapper>
                      </StyledTitleWrapper>
                    </div>
                  </StyledFullWidth>
                </StyledStoryContainer>
              );
            })}
            {loadMore && sidebar && (
              <StyledButton onClick={onLoadMoreClick}>
                <span>Load More</span>
              </StyledButton>
            )}
          </>
        )}
      </StyledContainer>
      {loadMore && !sidebar && (
        <StyledButton onClick={onLoadMoreClick}>
          <span>Load More</span>
        </StyledButton>
      )}
    </div>
  );
}

StorySlider.propTypes = {
  stories: PropTypes.arrayOf(PropTypes.object),
  loadMore: PropTypes.bool,
  sidebar: PropTypes.bool,
  mobileVertical: PropTypes.bool,
};

StorySlider.defaultProps = {
  stories: [],
  loadMore: false,
  sidebar: false,
  mobileVertical: false,
};

export default StorySlider;
